import { useMemo } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import Image from 'next/image'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'

import { constants } from '../../../lib/constants'
import { getAverageReadTime } from '../../../lib/utilities/content'
import { NewsletterWidget } from '../../styleguide/NewsletterWidget'
import styles from './content-page-body.module.scss'

import { ButtonHierarchy } from "@/components/styleguide/Button"
import { InternalLink } from "@/components/styleguide/Link"
import { Size } from "@/types/system"

export const ContentPageBody = ({ article }) => {
  dayjs.extend(utc)
  dayjs.extend(timezone)

  const customSanitize = useMemo(() => ({
    ...rehypeSanitize,
    tagNames: ['div', 'p', 'a', 'h1', 'h2', 'h3', 'ul', 'li'],
    attributes: {
      '*': ['class', 'id', 'href'],
      a: ['target', 'rel'],
      div: ['class'],
    },
  }), [])

  const flattenImages = children => {
    if (
      children &&
      children[0] &&
      children.length === 1 &&
      children[0].props &&
      children[0].props.src
    ) {
      const { props: imgEl } = children[0]
      return (
        <div className={styles.image}>
          <Image
            src={imgEl.src}
            alt={imgEl.alt}
            width={1320}
            height={742}
          />
        </div>
      )
    }

    return <p className={`${styles.content} ${styles.paragraph}`}>{children}</p>
  }

  return (
    <>
      <div className={`${styles.content} ${styles.header}`}>
        <h1 className={styles.title}>{article.content.title}</h1>
        <ul className={styles.meta}>
          <li className={`${styles.item} ${styles.author}`}>By {article.content.author}</li>
          <li className={`${styles.item} ${styles.ellipse}`}></li>
          <li className={styles.item}>
            {getAverageReadTime(article.content.body.toString().split(' ').length)} min read
          </li>
          <li className={`${styles.item} ${styles.ellipse}`}></li>
          <li className={styles.item}>
            <time dateTime={dayjs(article.content.pubDate).tz('Europe/London').format()}>
              {dayjs(article.content.pubDate).tz('Europe/London').format(constants.blogDateFormat)}
            </time>
          </li>
        </ul>
        <NewsletterWidget location="blog" />
        <p className={`${styles.description} ${styles.paragraph}`}>{article.content.description}</p>
      </div>
      <div className={styles.image}>
        <Image
          src={article.content.thumbnail}
          alt={article.content.thumbnailAlt}
          width={1320}
          height={742.5}
        />
      </div>
      <article className={styles.article}>
        <Markdown
          components={{
            p({ children }) {
              return flattenImages(children)
            },
            br() {
              return <></> // removes break tags
            },
            h1() {
              return <></> // removes duplicate h1s
            },
            h2({ children }) {
              return <h2 className={styles.h2}>{children}</h2>
            },
            h3({ children }) {
              return <h3 className={styles.h3}>{children}</h3>
            },
            h4({ children }) {
              return <h3 className={styles.h4}>{children}</h3>
            },
            ul({ children }) {
              return <ul className={styles['unordered-list']}>{children}</ul>
            },
            a({ href, children }) {
              let text = Array.isArray(children) ? children[0] : children
              let targetAttr = ''
              let classAttr = ''

              const attributeMatch = text.match(/{:.*?}/)
              if (attributeMatch) {
                let attributes = attributeMatch[0].replace(/{:|}/g, '').trim().replace(/&quot;/g, '"')
                
                const targetMatch = attributes.match(/target="([^"]+)"/)
                if (targetMatch) {
                  targetAttr = targetMatch[1]
                }

                const classMatch = attributes.match(/class="([^"]+)"/)
                if (classMatch) {
                  classAttr = classMatch[1]
                }

                text = text.replace(attributeMatch[0], '').trim()
              }

              if (classAttr === 'button') {
                return <InternalLink
                  size={Size.Large}
                  hierarchy={ButtonHierarchy.Filled}
                  href={href}
                  hasPadding
                  hasBackground
                  target={targetAttr || undefined}
                  className={styles[classAttr]}
                >
                  {text}
                </InternalLink>
              }

              return <a href={href} target={targetAttr || undefined} className={styles[classAttr] || undefined}>
                {text}
              </a>
            },
          }}
          rehypePlugins={[rehypeRaw, customSanitize]}>
          {article.content.body
            .replace(/:::cta/g, `<div class="${styles['cta-block']}">`)
            .replace(/:::/g, '</div>')}
        </Markdown>
      </article>
      {!article && (
        <div
          className="alert alert-warning mt-3"
          role="alert">
          We&apos;ve had trouble finding this blog post. Please try again later.
        </div>
      )}
    </>
  )
}
